import React, { useState, useEffect } from "react";
import { motion, Reorder } from "framer-motion";
import { updateMenu } from "../../apis/menu";
import "../../styles/pages/settings/Settings.css";

const Settings = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const savedItems = localStorage.getItem("sidebarItems");
    if (savedItems) {
      const parsedItems = JSON.parse(savedItems);
      setMenuItems(parsedItems);
    }
  }, []);

  const handleStatusToggle = (itemId) => {
    const updatedItems = menuItems.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          status: item.status === "active" ? "inactive" : "active",
        };
      }
      return item;
    });

    setMenuItems(updatedItems);
    setHasChanges(true);
  };

  const handleReorder = (reorderedItems) => {
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      order_no: index + 1,
    }));

    setMenuItems(updatedItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        role_id: parseInt(localStorage.getItem("role_id"), 10),
        menu_controls: menuItems,
      };

      const data = await updateMenu(payload);

      if (data?.description || data?.error?.description) {
        throw new Error(data?.description || data?.error?.description);
      }

      const updatedMenuItems = data?.user_menu_controls || menuItems;
      localStorage.setItem("sidebarItems", JSON.stringify(updatedMenuItems));
      window.dispatchEvent(new Event("sidebarItemsUpdated"));
      setHasChanges(false);
    } catch (err) {
      setError(err?.message || "Failed to save menu changes");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <h1 className="settings-title">Menu Settings</h1>
      {error && <div className="error-message">{error}</div>}

      <div className="settings-content">
        <div className="available-items">
          <h2>Manage Menu Items</h2>
          <p className="settings-description">
            Drag to reorder menu items and toggle their active status.
          </p>

          <Reorder.Group
            axis="y"
            values={menuItems}
            onReorder={handleReorder}
            className="items-list"
          >
            {menuItems.map((item) => (
              <Reorder.Item
                key={item.id}
                value={item}
                className={`item-card ${item.status}`}
                whileHover={{ scale: 1.01 }}
                whileDrag={{ scale: 1.05 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
              >
                <span className="drag-handle" aria-hidden="true">
                  <span className="drag-dots"></span>
                </span>

                <motion.span className="item-icon">
                  <img src={item.icon_url} alt={"icon"} className="menu-icon" />
                </motion.span>

                <div className="item-details">
                  <span className="item-label">{item.display_title}</span>
                </div>

                <label
                  className="toggle-switch"
                  aria-label={`Toggle ${item.display_title}`}
                >
                  <input
                    type="checkbox"
                    checked={item.status === "active"}
                    onChange={() => handleStatusToggle(item.id)}
                    disabled={loading}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </Reorder.Item>
            ))}
          </Reorder.Group>

          {(hasChanges || loading) && (
            <div className="menu-save-button-container">
              {loading ? (
                <div className="loader-button-container">
                  <span className="loader-button"></span>
                </div>
              ) : (
                <button className="menu-save-button" onClick={handleSave}>
                  Save Changes
                </button>
              )}
            </div>
          )}

          <div className="settings-guide-box">
            <div className="guide-title">How it works</div>
            <p>
              Drag the menu items using the handle on the left to reorder.
              Toggle the switch on the right to activate or deactivate the menu
              item.
            </p>
          </div>
        </div>

        <div className="settings-illustration" aria-hidden="true">
          <div className="floating-panel">
            <div className="panel-top"></div>
            <div className="panel-row"></div>
            <div className="panel-row"></div>
            <div className="panel-row"></div>
          </div>
          <div className="settings-badge">
            <span className="settings-gear"></span>
          </div>
          <span className="illustration-dot illustration-dot-top"></span>
          <span className="illustration-dot illustration-dot-left"></span>
          <span className="illustration-dot illustration-dot-right"></span>
        </div>
      </div>
    </div>
  );
};

export default Settings;
